import type {Identifier, XYCoord} from 'dnd-core'
import type {FC} from 'react'
import {useRef} from 'react'
import {useDrag, useDrop} from 'react-dnd'
import {twMerge} from 'tailwind-merge'

export type RowRenderer<T = Record<string, any>> = (
  r: T,
  index?: number
) => JSX.Element

export interface DraggableRowProps {
  id: any
  index: number
  row: Record<string, any>
  rowClassName?: string
  renderRow: RowRenderer
  moveDraggableRow: (dragIndex: number, hoverIndex: number) => void
}

interface DragItem {
  index: number
  id: string
  type: string
}

const DraggableRow: FC<DraggableRowProps> = ({
  id,
  row,
  index,
  moveDraggableRow,
  renderRow,
  rowClassName,
}) => {
  const ref = useRef<HTMLTableRowElement>(null)
  const [{handlerId}, drop] = useDrop<
    DragItem,
    void,
    {handlerId: Identifier | null}
  >({
    accept: 'ACCEPTABLE_ROW',
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return
      }
      const dragIndex = item.index
      const hoverIndex = index

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      // Determine mouse position
      const clientOffset = monitor.getClientOffset()

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      // Time to actually perform the action
      moveDraggableRow(dragIndex, hoverIndex)

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
  })

  const [{isDragging}, drag] = useDrag({
    type: 'ACCEPTABLE_ROW',
    item: () => {
      return {id, index}
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  return (
    <tr
      ref={ref}
      className={twMerge(
        'cursor-move table-row my-4 border border-dashed border-gray-500 ',
        rowClassName
      )}
      style={{opacity}}
      data-handler-id={handlerId}>
      {renderRow(row)}
    </tr>
  )
}

export default DraggableRow
